import { motion } from 'framer-motion';
import { Newspaper, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { ECONOMIC_NEWS } from '../../data/economy/news';

const IMPACT_STYLES: Record<string, { label: string; icon: typeof TrendingUp; class: string }> = {
  high: { label: 'High Impact', icon: TrendingUp, class: 'text-red-600 bg-red-50 border-red-100' },
  medium: { label: 'Medium Impact', icon: Minus, class: 'text-amber-600 bg-amber-50 border-amber-100' },
  low: { label: 'Low Impact', icon: TrendingDown, class: 'text-blue-600 bg-blue-50 border-blue-100' },
};

const SECTOR_COLORS: Record<string, string> = {
  'Dangote Group': 'bg-amber-50 text-amber-700 border-amber-100',
  'Oil & Gas': 'bg-orange-50 text-orange-700',
  'Monetary Policy': 'bg-purple-50 text-purple-700',
  'Inflation': 'bg-red-50 text-red-700',
  'Agriculture': 'bg-green-50 text-green-700',
  'Technology': 'bg-blue-50 text-blue-700',
  'Manufacturing': 'bg-indigo-50 text-indigo-700',
  'Trade': 'bg-cyan-50 text-cyan-700',
  'Banking': 'bg-emerald-50 text-emerald-700',
};

export function EconomicNews() {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 sm:mb-12"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Newspaper className="h-5 w-5 text-[#075b49]" />
            <span className="text-xs sm:text-sm font-medium text-[#075b49] uppercase tracking-wider">Nigerian Economy</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Market & Economic News</h2>
          <p className="text-sm sm:text-base text-gray-500 mt-2 sm:mt-3 max-w-2xl mx-auto">
            Track developments across the sectors that shape the Naira and Nigeria economy
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {ECONOMIC_NEWS.map((item, idx) => {
            const impact = IMPACT_STYLES[item.impact] ?? IMPACT_STYLES.medium;
            const ImpactIcon = impact.icon;
            const sectorColor = SECTOR_COLORS[item.sector] ?? 'bg-gray-50 text-gray-700';

            return (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${sectorColor}`}>
                    {item.sector}
                  </span>
                  <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${impact.class}`}>
                    <ImpactIcon className="h-3 w-3" />
                    {impact.label}
                  </span>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.summary}</p>
                <p className="text-xs text-gray-400 mt-3">
                  {new Date(item.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
